import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardFooter,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  Image,
  Input,
  Select,
  Stack,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import {
  AiOutlineDelete,
  AiOutlineArrowUp,
  AiOutlineArrowDown,
  FiPlusSquare,
} from 'react-icons/all';
import { useDispatch, useSelector } from 'react-redux';
import { setSite } from '../../../features/site/siteSlice';
import { getBackendBaseURL } from '../../../utils/utils';
import ImageUploader from '../../common/image-uploader';
import { updateSite } from '../../../api/siteApi';

function Gallery({ isEdit, prevClicked, nextClicked }) {
  const { user } = useSelector(state => state.user);
  const { site } = useSelector(state => state.site);
  const dispatch = useDispatch();
  const [inProgress, setInProgress] = useState(false);
  const [displayNext, setDisplayNext] = useState(isEdit);
  const [actionStatus, setActionStatus] = useState('');
  const [gallery, setGallery] = useState([]);
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const toast = useToast();

  useEffect(() => {
    if (isEdit || site) {
      setupComponent();
    }
    // eslint-disable-next-line
  }, [site]);

  const setupComponent = () => {
    let initialGallery = site?.gallery ?? [];
    setGallery(
      initialGallery.map(item => ({
        type: item.type || 'image',
        src: item.src || '',
        title: item.title || '',
        _id: item._id,
        id: item._id || Math.random().toString(36).substring(2, 9),
      }))
    );
  };

  const handleTitleChange = (index, event) => {
    const values = [...gallery];
    values[index].title = event.target.value;
    setGallery(values);
  };

  const handleTypeChange = (index, event) => {
    const values = [...gallery];
    values[index].type = event.target.value;
    setGallery(values);
  };

  const handleChangePath = (index, path) => {
    const values = [...gallery];
    values[index].src = path;
    setGallery(values);
  };

  const handleAddMedia = () => {
    const maxItems = 12;
    if (gallery.length >= maxItems) {
      toast({
        title: 'Limit reached',
        description: `You can add up to ${maxItems} gallery items.`,
        status: 'warning',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }
    setGallery([
      {
        type: 'image',
        src: '',
        title: '',
        id: Math.random().toString(36).substring(2, 9),
      },
      ...gallery,
    ]);
  };

  const handleDeleteMedia = index => {
    const values = [...gallery];
    values.splice(index, 1);
    setGallery(values);
  };

  const handleMoveUp = i => {
    if (i === 0) return;
    const values = [...gallery];
    const temp = values[i];
    values[i] = values[i - 1];
    values[i - 1] = temp;
    setGallery(values);
  };

  const handleMoveDown = i => {
    if (i === gallery.length - 1) return;
    const values = [...gallery];
    const temp = values[i];
    values[i] = values[i + 1];
    values[i + 1] = temp;
    setGallery(values);
  };

  const saveGallery = () => {
    setInProgress(true);
    const cleanedGallery = gallery.map(item => ({
      type: item.type,
      src: item.src,
      title: item.title,
    }));

    const payload = {
      gallery: cleanedGallery,
      id: site._id,
      updateType: 'GALLERY',
      creator: user._id,
    };

    updateSite(payload)
      .then(response => {
        setInProgress(false);
        if (response.site) {
          dispatch(setSite(response.site));
          const message = isEdit
            ? 'Updated Gallery details successfully.'
            : 'Saved Gallery details. Click next to proceed.';
          toast({
            title: 'Success',
            description: message,
            status: 'success',
            duration: 5000,
            isClosable: true,
            position: 'top-right',
          });
          setActionStatus(message);
          setDisplayNext(true);
        } else {
          toast({
            title: 'Error',
            description: response.message || 'Failed to update gallery',
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(error => {
        console.error(error);
        setInProgress(false);
        toast({
          title: 'Error',
          description: 'Something went wrong, please try again',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      });
  };

  return (
    <Card
      bg={useColorModeValue('white', 'gray.700')}
      border={'1px solid'}
      borderColor={borderColor}
    >
      <CardBody>
        <Stack spacing={4}>
          <Text fontSize="lg" fontWeight="bold">
            Media Gallery (Images & Videos)
          </Text>
          <Text fontSize="sm" color="gray.500">
            Add images or videos to display in your card template gallery.
          </Text>

          <Button
            leftIcon={<FiPlusSquare />}
            variant="outline"
            colorScheme="teal"
            onClick={handleAddMedia}
            isDisabled={gallery.length >= 12}
          >
            Add Media Item
          </Button>

          {actionStatus && (
            <Text color="green.500" fontSize="sm">
              {actionStatus}
            </Text>
          )}

          {gallery.map((field, idx) => {
            const mediaSrc = field.src?.startsWith('http')
              ? field.src
              : `${getBackendBaseURL()}/${field.src}`;

            return (
              <Card key={field.id || idx} variant="outline" p={4}>
                <Stack spacing={3} direction={{ base: 'column', md: 'row' }}>
                  <Stack direction="column">
                    <Text fontWeight="semibold">Item #{idx + 1}</Text>
                    <ButtonGroup size="sm" isAttached variant="outline">
                      <IconButton
                        aria-label="Move Up"
                        icon={<AiOutlineArrowUp />}
                        onClick={() => handleMoveUp(idx)}
                        isDisabled={idx === 0}
                      />
                      <IconButton
                        aria-label="Move Down"
                        icon={<AiOutlineArrowDown />}
                        onClick={() => handleMoveDown(idx)}
                        isDisabled={idx === gallery.length - 1}
                      />
                      <IconButton
                        aria-label="Delete"
                        colorScheme="red"
                        icon={<AiOutlineDelete />}
                        onClick={() => handleDeleteMedia(idx)}
                      />
                    </ButtonGroup>
                  </Stack>

                  <FormControl>
                    <FormLabel>Media Type</FormLabel>
                    <Select
                      value={field.type}
                      onChange={e => handleTypeChange(idx, e)}
                    >
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                    </Select>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Title / Caption (Optional)</FormLabel>
                    <Input
                      placeholder="Title of media"
                      value={field.title}
                      onChange={e => handleTitleChange(idx, e)}
                    />
                  </FormControl>
                  <Stack direction="column">
                    {field.src && (
                      <Box mt={2}>
                        {field.type === 'video' ? (
                          <video
                            src={mediaSrc}
                            controls
                            style={{
                              maxHeight: '200px',
                              maxWidth: '200px',
                              borderRadius: '8px',
                            }}
                          />
                        ) : (
                          <Image
                            src={mediaSrc}
                            alt={field.title || 'Gallery preview'}
                            maxH="200px"
                            maxWidth="200px"
                            objectFit="cover"
                            borderRadius="md"
                          />
                        )}
                      </Box>
                    )}
                    <FormControl width={'200px'}>
                      <FormLabel display={field.src ? 'none' : 'block'}>
                        Upload {field.type === 'video' ? 'Video' : 'Image'}
                      </FormLabel>
                      <ImageUploader
                        label={
                          field.src
                            ? `Change ${field.type}`
                            : `Upload ${field.type}`
                        }
                        accept={field.type === 'video' ? 'video/*' : 'image/*'}
                        OnUpload={path => handleChangePath(idx, path)}
                      />
                    </FormControl>
                  </Stack>
                </Stack>
              </Card>
            );
          })}
        </Stack>
      </CardBody>
      <Divider />
      <CardFooter>
        <ButtonGroup w={'100%'} justifyContent="end">
          <Button colorScheme="teal" onClick={prevClicked}>
            Previous
          </Button>
          <Button
            colorScheme="teal"
            isLoading={inProgress}
            onClick={saveGallery}
          >
            Save Gallery
          </Button>
          {displayNext && (
            <Button colorScheme="teal" onClick={nextClicked}>
              Next
            </Button>
          )}
        </ButtonGroup>
      </CardFooter>
    </Card>
  );
}

export default Gallery;
